/**
 * CardList — render de cards (mobile) do ListView.
 *
 * Cada row vira 1 card. A composição do card deriva da hierarquia de
 * colunas (cardSlot explícito ou priority):
 *   - title   → topo-esquerda (1ª coluna 'primary' ou cardSlot='title')
 *   - aside   → topo-direita (demais 'primary' ou cardSlot='aside')
 *   - badge   → linha de chips (cardSlot='badge')
 *   - meta    → linhas "label: valor" (colunas 'secondary' ou cardSlot='meta')
 *   - hidden-mobile → omitido no card
 *
 * Para layout totalmente custom, passe renderCard ao ListView.
 */
import React from "react";
import {
  CardListRoot,
  Card,
  CardTopRow,
  CardTitle,
  CardSubtitle,
  CardAside,
  CardBadges,
  CardMeta,
  CardMetaRow,
  CardMetaLabel,
  CardMetaValue,
} from "./styles";

function resolveCell(col, row, index) {
  if (typeof col.render === "function") return col.render(row, index);
  return row?.[col.key];
}

// Classifica as colunas nos slots do card. Slots explícitos (cardSlot)
// têm precedência; senão deriva da priority:
//   - 1ª 'primary' (sem slot) → title
//   - demais 'primary' (sem slot) → subtitle (linha sob o título)
//   - 'aside' explícito → aside (topo-direita, tipicamente o valor)
//   - 'badge' → linha de chips
//   - 'secondary' / 'meta' → meta (label: valor)
//   - 'hidden-mobile' → omitido
function classifyColumns(columns) {
  const titleCols = [];
  const subtitleCols = [];
  const asideCols = [];
  const badgeCols = [];
  const metaCols = [];

  for (const col of columns) {
    const slot = col.cardSlot;
    const priority = col.priority || "secondary";
    if (slot === "title") titleCols.push(col);
    else if (slot === "subtitle") subtitleCols.push(col);
    else if (slot === "aside") asideCols.push(col);
    else if (slot === "badge") badgeCols.push(col);
    else if (slot === "meta") metaCols.push(col);
    else if (priority === "hidden-mobile") {
      /* omitido no card */
    } else if (priority === "primary") {
      if (titleCols.length === 0) titleCols.push(col);
      else subtitleCols.push(col);
    } else {
      metaCols.push(col);
    }
  }
  return { titleCols, subtitleCols, asideCols, badgeCols, metaCols };
}

export default function CardList({ data, columns, rowKey, onRowClick }) {
  const { titleCols, subtitleCols, asideCols, badgeCols, metaCols } =
    classifyColumns(columns);

  return (
    <CardListRoot>
      {data.map((row, index) => (
        <Card
          key={rowKey(row, index)}
          $clickable={Boolean(onRowClick)}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          role={onRowClick ? "button" : undefined}
          tabIndex={onRowClick ? 0 : undefined}
          onKeyDown={
            onRowClick
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onRowClick(row);
                  }
                }
              : undefined
          }
        >
          {(titleCols.length > 0 ||
            subtitleCols.length > 0 ||
            asideCols.length > 0) && (
            <CardTopRow>
              <div style={{ minWidth: 0 }}>
                {titleCols.length > 0 && (
                  <CardTitle>
                    {titleCols.map((col) => (
                      <span key={col.key}>{resolveCell(col, row, index)}</span>
                    ))}
                  </CardTitle>
                )}
                {subtitleCols.length > 0 && (
                  <CardSubtitle>
                    {subtitleCols.map((col) => (
                      <span key={col.key}>{resolveCell(col, row, index)}</span>
                    ))}
                  </CardSubtitle>
                )}
              </div>
              {asideCols.length > 0 && (
                <CardAside>
                  {asideCols.map((col) => (
                    <span key={col.key}>{resolveCell(col, row, index)}</span>
                  ))}
                </CardAside>
              )}
            </CardTopRow>
          )}

          {badgeCols.length > 0 && (
            <CardBadges>
              {badgeCols.map((col) => (
                <span key={col.key}>{resolveCell(col, row, index)}</span>
              ))}
            </CardBadges>
          )}

          {metaCols.length > 0 && (
            <CardMeta>
              {metaCols.map((col) => {
                const value = resolveCell(col, row, index);
                if (value == null || value === "") return null;
                return (
                  <CardMetaRow key={col.key}>
                    <CardMetaLabel>{col.label}</CardMetaLabel>
                    <CardMetaValue
                      title={typeof value === "string" ? value : undefined}
                    >
                      {value}
                    </CardMetaValue>
                  </CardMetaRow>
                );
              })}
            </CardMeta>
          )}
        </Card>
      ))}
    </CardListRoot>
  );
}
